"use client";

import type React from "react";
import { useState, useEffect } from "react";
// import { useParams, useRouter } from "next/navigation" // Import useParams and useRouter
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Grid,
  Paper,
  Button,
  IconButton,
  Skeleton,
  useTheme,
  useMediaQuery,
  Fade,
  Alert,
  Snackbar,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  QuestionAnswer,
  CheckCircle,
  RadioButtonUnchecked,
  ArrowBack,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import {
  deleteJobQuestion,
  fetchJobById,
  getJobsQuestion,
} from "../../../api/jobs/jobs"; // Use the mock API from api/jobs/jobs.ts
// import CreateQuestionForm from "./create-question-form" // Import the new form component
import { IonContent, IonPage } from "@ionic/react"; // Preserve Ionic components
import { useLocation, useHistory } from "react-router";
import CreateQuestionForm from "./CreateQuestionForm";

interface Question {
  id: string;
  question: string;
  answer: string;
  options: string[];
}

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  border: "none",
  borderRadius: 16,
  background: "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(10px)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
  },
}));

const OptionChip = styled(Chip)<{ iscorrect?: boolean }>(
  ({ theme, iscorrect }) => ({
    borderRadius: 20,
    fontWeight: 500,
    transition: "all 0.3s ease",
    ...(iscorrect && {
      background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
      color: "white",
      fontWeight: 600,
    }),
    ...(!iscorrect && {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
      color: "#666",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.08)",
      },
    }),
  })
);

const StatsCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  marginBottom: theme.spacing(4),
  border: "none",
  boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)",
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1),
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.1)",
  },
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
  "&:hover": {
    background: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
    transform: "scale(1.1)",
    boxShadow: "0 12px 35px rgba(102, 126, 234, 0.6)",
  },
}));

const JobQuestionsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const router = useHistory();

  // Extract job ID from URL path
  const pathSegments = location.pathname.split("/");
  const jobId = pathSegments[2];

  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [openCreateModal, setOpenCreateModal] = useState(false); // State for modal
  const [error, setError] = useState<string | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(
    null
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [job, setJob] = useState<any>(null); // State to hold job details

  const loadJob = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchJobById(jobId);
      console.log(res);
      setJob(res.data);
    } catch (err) {
      console.error("Error loading job:", err);
      setError("Failed to load job details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      loadJob();
    }
  }, [jobId]);

  useEffect(() => {
    if (jobId) {
      fetchQuestions();
    }
  }, [jobId]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const data = { job_id: jobId };
      const response = await getJobsQuestion(data);
      console.log({ response });
      const result = response.data;
      setQuestions(
        result.data?.map((q: any) => ({
          ...q,
          options: JSON.parse(q.options),
        })) || []
      );
    } catch (err) {
      console.error(err);
      setError("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = (question: Question) => {
    setSelectedQuestion(question);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedQuestion?.question_id) return;

    const data = {
      question_id: selectedQuestion.question_id,
      job_id: jobId,  
    };
    try {
      await deleteJobQuestion(data);
      setError(null); // Clear any previous error
      fetchQuestions(); // Refresh questions after delete
    } catch (err: any) {
      setError(err.message || "Failed to delete question.");
    } finally {
      setShowDeleteDialog(false);
      setSelectedQuestion(null);
    }
  };

  const renderSkeletonLoader = () => (
    <Grid container spacing={3}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Grid item xs={12} key={index}>
          <Card sx={{ borderRadius: 4, p: 3 }}>
            <Stack spacing={2}>
              <Skeleton
                variant="text"
                width="80%"
                height={32}
                sx={{ borderRadius: 2 }}
              />
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={28}
                  sx={{ borderRadius: 4 }}
                />
                <Skeleton
                  variant="rectangular"
                  width={100}
                  height={28}
                  sx={{ borderRadius: 4 }}
                />
                <Skeleton
                  variant="rectangular"
                  width={90}
                  height={28}
                  sx={{ borderRadius: 4 }}
                />
                <Skeleton
                  variant="rectangular"
                  width={110}
                  height={28}
                  sx={{ borderRadius: 4 }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Skeleton
                  variant="text"
                  width="30%"
                  height={24}
                  sx={{ borderRadius: 2 }}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="circular" width={40} height={40} />
                </Box>
              </Box>
            </Stack>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderStats = () => {
    if (loading || !questions.length) return null;
    const totalQuestions = questions.length;
    const multipleChoiceQuestions = questions.filter(
      (q) => q.options.length > 2
    ).length;
    const avgOptionsPerQuestion =
      questions.reduce((sum, q) => sum + q.options.length, 0) /
      questions.length;

    return (
      <StatsCard elevation={0}>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} sm={4} textAlign="center">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <QuestionAnswer sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
                <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {totalQuestions}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ opacity: 0.9, fontWeight: 500 }}
                >
                  Total Questions
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} textAlign="center">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <CheckCircle sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
                <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {multipleChoiceQuestions}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ opacity: 0.9, fontWeight: 500 }}
                >
                  Multiple Choice
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} textAlign="center">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <RadioButtonUnchecked
                  sx={{ fontSize: 48, mb: 1, opacity: 0.9 }}
                />
                <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {Math.round(avgOptionsPerQuestion)}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ opacity: 0.9, fontWeight: 500 }}
                >
                  Avg Options
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </StatsCard>
    );
  };

  return (
    <IonPage>
      <IonContent>
        <Box
          sx={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
        
          <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Statistics */}
            {renderStats()}

            {/* Questions List */}
            {loading ? (
              renderSkeletonLoader()
            ) : questions.length === 0 ? (
              <Paper
                sx={{
                  p: 6,
                  textAlign: "center",
                  borderRadius: 4,
                  background: "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(10px)",
                  border: "none",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                }}
              >
                <QuestionAnswer sx={{ fontSize: 80, color: "#ddd", mb: 2 }} />
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: 600, color: "#666" }}
                >
                  No Questions Yet
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#999", fontSize: "1.1rem", mb: 3 }}
                >
                  Create your first question to get started
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setOpenCreateModal(true)}
                  size="large"
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                    },
                  }}
                >
                  Create First Question
                </Button>
              </Paper>
            ) : (
              <Fade in timeout={800}>
                <Grid container spacing={3}>
                  {questions.map((question, index) => (
                    <Grid item xs={12} key={question.id}>
                      <Fade in timeout={600 + index * 100}>
                        <StyledCard>
                          <CardContent sx={{ p: 4 }}>
                            <Stack spacing={3}>
                              {/* Question Header */}
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 600,
                                    color: "#333",
                                    lineHeight: 1.4,
                                    flex: 1,
                                    mr: 2,
                                  }}
                                >
                                  {question.question}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <ActionButton
                                    size="small"
                                    sx={{
                                      color: "#667eea",
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(102, 126, 234, 0.1)",
                                      },
                                    }}
                                  >
                                    <Edit fontSize="small" />
                                  </ActionButton>
                                  <ActionButton
                                    size="small"
                                    onClick={() =>
                                      handleDeleteQuestion(question)
                                    }
                                    sx={{
                                      color: "#f44336",
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(244, 67, 54, 0.1)",
                                      },
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </ActionButton>
                                </Box>
                              </Box>
                              {/* Options */}
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    color: "#666",
                                    mb: 1.5,
                                    fontWeight: 600,
                                  }}
                                >
                                  Answer Options:
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                  }}
                                >
                                  {question.options.map((option, optIndex) => (
                                    <OptionChip
                                      key={optIndex}
                                      label={option}
                                      size="medium"
                                      iscorrect={option === question.answer}
                                      icon={
                                        option === question.answer ? (
                                          <CheckCircle sx={{ fontSize: 16 }} />
                                        ) : (
                                          <RadioButtonUnchecked
                                            sx={{ fontSize: 16 }}
                                          />
                                        )
                                      }
                                    />
                                  ))}
                                </Box>
                              </Box>
                              <Divider sx={{ opacity: 0.3 }} />
                              {/* Correct Answer */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <CheckCircle
                                  sx={{ color: "#4CAF50", fontSize: 20 }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#666" }}
                                >
                                  Correct Answer:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, color: "#4CAF50" }}
                                >
                                  {question.answer}
                                </Typography>
                              </Box>
                            </Stack>
                          </CardContent>
                        </StyledCard>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              </Fade>
            )}

            {/* Mobile FAB */}
            {isMobile && (
              <StyledFab onClick={() => setOpenCreateModal(true)}>
                <Add />
              </StyledFab>
            )}

            {/* Create Question Dialog */}
            <Dialog
              open={openCreateModal}
              onClose={() => setOpenCreateModal(false)}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                },
              }}
            >
              <DialogContent>
                <CreateQuestionForm
                  jobId={jobId}
                  onSuccess={() => {
                    setOpenCreateModal(false);
                    fetchQuestions();
                  }}
                  onClose={() => setOpenCreateModal(false)} // Pass onClose to the form
                />
              </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
              open={showDeleteDialog}
              onClose={() => setShowDeleteDialog(false)}
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                },
              }}
            >
              <DialogTitle sx={{ pb: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#333" }}
                >
                  Delete Question
                </Typography>
              </DialogTitle>
              <DialogContent>
                <Typography
                  variant="body1"
                  sx={{ color: "#666", lineHeight: 1.6 }}
                >
                  Are you sure you want to delete this question? This action
                  cannot be undone.
                </Typography>
                {selectedQuestion && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "rgba(244, 67, 54, 0.05)",
                      borderRadius: 2,
                      border: "1px solid rgba(244, 67, 54, 0.2)",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#333" }}
                    >
                      "{selectedQuestion.question}"
                    </Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 3, pt: 1 }}>
                <Button
                  onClick={() => setShowDeleteDialog(false)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    color: "#666",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDelete}
                  variant="contained"
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    background:
                      "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)",
                    },
                  }}
                >
                  Delete Question
                </Button>
              </DialogActions>
            </Dialog>

            {/* Error Snackbar */}
            <Snackbar
              open={!!error}
              autoHideDuration={6000}
              onClose={() => setError(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Alert
                onClose={() => setError(null)}
                severity="error"
                sx={{
                  borderRadius: 2,
                  "& .MuiAlert-message": { fontWeight: 500 },
                }}
              >
                {error}
              </Alert>
            </Snackbar>
          </Container>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default JobQuestionsPage;
